import { useState, useEffect } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { DateRange, FilterOption, IdNameObject } from '../../types';
import {
  fetchGlanceDates,
  fetchGlanceMarkets,
  fetchGlanceRetailers,
  fetchGlanceAdPlatforms,
  getDefaultParams
} from './helpers';
import { returnCurrencySymbol } from '../../helpers/format';
import { ReduxStates } from '../../redux/reduxTypes';

import DropDownFilter from '../../components/DropDownFilter';
import SegmentHeader from '../../components/SegmentHeader';
import Grid from '../../components/Grid';
import Gap from '../../components/Gap';
import SegmentContainer from '../../components/SegmentContainer';
import AmazonGrid from './components/AmazonGrid';
import AmazonSummary from './components/AmazonSummary';
import AmazonShare from './components/AmazonShare';
import SalesSegment from './components/SalesSegment';
import SalesGraph from './components/SalesGraph';
import ToolTip from '../../components/Tooltip';

const Glance = (): any => {
  const headerContainerStyles = 'w-full flex justify-between';
  const [availableMarkets, setAvailableMarkets] = useState<Array<FilterOption>>([]);
  const [availableAdPlatforms, setAvailableAdPlatforms] = useState<Array<FilterOption>>([]);
  const [availableAmazonDates, setAvailableAmazonDates] = useState<DateRange>();
  const [availableShareDates, setAvailableShareDates] = useState<DateRange>();
  const [availableSalesDates, setAvailableSalesDates] = useState<DateRange>();
  const [fetching, setFetching] = useState<boolean>(false);
  const storeData = useSelector((state: ReduxStates) => state.data);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const user = useSelector((state: ReduxStates) => state.user);
  const [defaultParams, setDefaultParams] = useState<any>({});
  const defaultMarket = {
    id: '0',
    name: copyData?.all_options
  };
  const defaultAdPlatform = {
    id: '0',
    name: 'ALL'
  };
  const [chosenMarket, setChosenMarket] = useState<IdNameObject>(defaultMarket);
  const [chosenAdPlatform, setChosenAdPlatform] = useState<IdNameObject>(defaultAdPlatform);
  const [isShare, setIsShare] = useState<boolean>(true);
  const [isMarketsFetching, setIsMarketsFetching] = useState<boolean>(false);
  const [isAdPlatformFetching, setAdPlatformFetching] = useState<boolean>(false);
  const [isRetailerFetching, setIsRetailerFetching] = useState<boolean>(true);
  const [isMetailFetching, setIsMetailFetching] = useState<boolean>(true);
  const [isRetailerRefreshingFromAdPlatform, setIsRetailerRefreshingFromAdPlatform] = useState<boolean>(false);
  const [retailerFilters, setRetailerFilters] = useState<Array<FilterOption>>([]);
  const defaultRetailer = {
    id: '0',
    name: 'ALL'
  };
  const [chosenRetailer, setChosenRetailer] = useState<IdNameObject>(defaultRetailer);

  useEffect(() => {
    const isShare = storeData.messagingInfo?.filter((option) => option.dataset === 'Share').at(0)?.status;
    isShare !== undefined && setIsShare(isShare);
  }, [storeData.messagingInfo]);

  const history = useHistory();

  // set document title
  document.title = 'At a Glance - Ascential Connect';

  //reroute if not in userModules
  useEffect(() => {
    if (user && user.userModules && !user.userModules.includes('At a Glance')) history.push('/no-access');
  }, [user]);

  const fetchDates = async () => {
    const components = ['share', 'atglancesales'];
    const params = {
      interval: 'weekly'
    };

    components.forEach(async (component) => {
      const data = await fetchGlanceDates(component, params);
      const dates = data.length ? data[0] : null;
      switch (component) {
        case 'share':
          setAvailableShareDates(dates);
          break;
        case 'atglancesales':
          setAvailableSalesDates(dates);
          break;
        default:
          break;
      }
    });
  };

  const fetchMetailDates = async () => {
    const params = {
      interval: 'weekly',
      retailerIds: chosenRetailer.id != '0' ? chosenRetailer.id : '',
      adPlatformIds: chosenAdPlatform.id != '0' ? chosenAdPlatform.id : ''
    };
    const data = await fetchGlanceDates('metail', params);
    const dates = data.length ? data[0] : null;
    setAvailableAmazonDates(dates);
    setIsMetailFetching(false);
  };

  const fetchRetailers = async () => {
    const params = {
      adPlatformIds: chosenAdPlatform.id != '0' ? chosenAdPlatform.id : ''
    };
    const fetchedRetailerFilters: Array<FilterOption> = await fetchGlanceRetailers(params);
    const allRetailerFilters =
      fetchedRetailerFilters.length > 1 ? [...fetchedRetailerFilters, defaultRetailer] : fetchedRetailerFilters;
    setRetailerFilters(allRetailerFilters);
    if (!isRetailerRefreshingFromAdPlatform)
      setChosenRetailer(allRetailerFilters.find((element) => element.name === 'Amazon') || allRetailerFilters[0]);
    else
      setChosenRetailer(
        allRetailerFilters.find((element) => element.id === chosenRetailer.id) ||
          (allRetailerFilters.length < 2 ? allRetailerFilters[0] : defaultRetailer)
      );
    setIsRetailerFetching(false);
  };

  const fetchMarkets = async () => {
    const params = {
      retailerIds: chosenRetailer.id != '0' ? chosenRetailer.id : '',
      adPlatformIds: chosenAdPlatform.id != '0' ? chosenAdPlatform.id : ''
    };
    const fetchedMarketsFilters: Array<FilterOption> = await fetchGlanceMarkets(params);
    fetchedMarketsFilters.length > 1 && fetchedMarketsFilters.unshift(defaultMarket);
    setAvailableMarkets(fetchedMarketsFilters);
    setChosenMarket(fetchedMarketsFilters[0]);
    setIsMarketsFetching(false);
  };

  const fetchAdPlatforms = async () => {
    const params = {
      retailerIds: chosenRetailer.id != '0' ? chosenRetailer.id : ''
    };
    const fetchedAdPlatforms: Array<FilterOption> = await fetchGlanceAdPlatforms(params);
    const allAdPlatformFilters =
      fetchedAdPlatforms.length > 1 ? [...fetchedAdPlatforms, defaultAdPlatform] : fetchedAdPlatforms;
    fetchedAdPlatforms.unshift(defaultAdPlatform);
    setAvailableAdPlatforms(fetchedAdPlatforms);
    setChosenAdPlatform(
      allAdPlatformFilters.find((element) => element.id === chosenAdPlatform.id) || allAdPlatformFilters[0]
    );
    setAdPlatformFetching(false);
  };

  const setChosenRetailerFromName = (name: string) => {
    setIsMarketsFetching(true);
    setAdPlatformFetching(true);
    setIsMetailFetching(true);
    if (name === defaultRetailer.name) {
      setChosenRetailer(defaultRetailer);
    } else {
      const retailer = retailerFilters?.filter((retailer) => retailer.name === name);
      setChosenRetailer(retailer[0]);
    }
  };

  useEffect(() => {
    fetchRetailers();
  }, []);

  useEffect(() => {
    if (!isRetailerFetching) {
      fetchMetailDates();
      fetchMarkets();
      if (!isRetailerRefreshingFromAdPlatform) fetchAdPlatforms();
    }
    setIsRetailerRefreshingFromAdPlatform(false);
  }, [chosenRetailer, isRetailerFetching]);

  useEffect(() => {
    if (isRetailerRefreshingFromAdPlatform) {
      fetchRetailers();
      fetchMarkets();
    }
  }, [chosenAdPlatform]);

  useEffect(() => {
    if (storeData.defaultCurrency) {
      fetchDates();
    }
  }, [storeData.defaultCurrency]);

  useEffect(() => {
    if (storeData.glanceDefaults) {
      setDefaultParams(getDefaultParams(storeData.glanceDefaults));
    }
  }, [storeData]);

  const setChosenMarketFromName = (name: string) => {
    if (name === defaultMarket.name) {
      setChosenMarket(defaultMarket);
    } else {
      const market = availableMarkets?.filter((market) => market.name === name);
      setChosenMarket(market[0]);
    }
  };

  const setChosenAdPlatformFromName = (name: string) => {
    setIsMetailFetching(true);
    setIsMarketsFetching(true);
    if (name === defaultAdPlatform.name) {
      setChosenAdPlatform(defaultAdPlatform);
    } else {
      const adplatform = availableAdPlatforms?.filter((adplatform) => adplatform.name === name);
      setChosenAdPlatform(adplatform[0]);
    }
    setIsRetailerRefreshingFromAdPlatform(true);
  };

  const getFormattedDate = (date: string) => {
    return moment.utc(date).format('MMM DD, YYYY');
  };

  const isAValidDate = (date: string) => {
    return moment.utc(date).year() > 1900;
  };

  return (
    <div className="pt-[30px] px-6 pb-[43px] relative">
      <div className="shadow-cardShadow">
        <SegmentContainer variant={'chosenWithoutMarginBottom'}>
          <Gap padding="8px 13px">
            <div className={headerContainerStyles}>
              {availableAmazonDates && (
                <SegmentHeader
                  title={chosenRetailer.name === 'ALL' ? 'All Retailers' : chosenRetailer.name}
                  subTitle={
                    isAValidDate(availableAmazonDates.startDate)
                      ? `${getFormattedDate(availableAmazonDates.startDate)} to ${getFormattedDate(
                          availableAmazonDates.endDate
                        )}`
                      : 'No data'
                  }
                />
              )}
              <div className="flex">
                <div className="mr-8">
                  <span className="text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 inline uppercase">
                    {'Retailer'}
                  </span>
                  <ToolTip
                    data={{
                      status: 'HELP',
                      message: copyData?.retailer_selector_tooltip
                    }}
                  />
                  <div className="inline-block pl-2">
                    <DropDownFilter
                      variant="small"
                      value={chosenRetailer.name || defaultRetailer.name}
                      options={retailerFilters?.map((m) => m.name).filter((x) => x !== chosenRetailer?.name)}
                      onChange={(option: string) => setChosenRetailerFromName(option)}
                      disabled={retailerFilters.length === 1 ? true : fetching}
                      scrollable={retailerFilters.length > 8 ? true : false}
                    />
                  </div>
                </div>
                {availableAdPlatforms.length > 1 && (
                  <div className="mr-8">
                    <span className="text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 inline uppercase">
                      {'Ad Platform'}
                    </span>
                    <div className="inline-block pl-2">
                      <DropDownFilter
                        variant="small"
                        value={chosenAdPlatform.name || defaultAdPlatform.name}
                        options={availableAdPlatforms?.map((m) => m.name).filter((x) => x !== chosenAdPlatform?.name)}
                        onChange={(option: string) => setChosenAdPlatformFromName(option)}
                        disabled={fetching}
                        scrollable={availableAdPlatforms.length > 8 ? true : false}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 inline uppercase">
                    {'Market Selector'}
                  </span>
                  <ToolTip
                    data={{
                      status: 'HELP',
                      message: copyData?.market_selector_tooltip
                    }}
                  />
                  <div className="inline-block pl-2">
                    <DropDownFilter
                      variant="small"
                      value={chosenMarket.name || defaultMarket.name}
                      options={availableMarkets?.map((m) => m.name).filter((x) => x !== chosenMarket?.name)}
                      onChange={(option: string) => setChosenMarketFromName(option)}
                      disabled={availableMarkets.length === 1 ? true : fetching}
                      scrollable={availableMarkets.length > 8 ? true : false}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-[14px] mx-0">
              <Grid colGap={'4px'} rowGap="4px" className="mb-1" gridCols={5}>
                {!isMarketsFetching &&
                  !isAdPlatformFetching &&
                  !isRetailerFetching &&
                  !isMetailFetching &&
                  availableMarkets &&
                  availableAdPlatforms &&
                  availableAmazonDates &&
                  retailerFilters && (
                    <AmazonSummary
                      chosenMarketId={chosenMarket.id}
                      defaultMarket={defaultMarket}
                      chosenAdPlatformId={chosenAdPlatform.id}
                      defaultAdPlatform={defaultAdPlatform}
                      chosenRetailerId={chosenRetailer.id}
                      defaultRetailer={defaultRetailer}
                      dateRange={availableAmazonDates}
                      setFetching={(fetching) => setFetching(fetching)}
                      defaultParams={defaultParams}
                    />
                  )}
              </Grid>
            </div>
          </Gap>
        </SegmentContainer>
        {!isMarketsFetching &&
          !isAdPlatformFetching &&
          !isRetailerFetching &&
          !isMetailFetching &&
          availableMarkets &&
          availableAdPlatforms &&
          availableAmazonDates &&
          retailerFilters && (
            <AmazonGrid
              chosenMarketId={chosenMarket.id}
              defaultMarket={defaultMarket}
              chosenAdPlatformId={chosenAdPlatform.id}
              defaultAdPlatform={defaultAdPlatform}
              chosenRetailerId={chosenRetailer.id}
              defaultRetailer={defaultRetailer}
              dateRange={availableAmazonDates}
              defaultParams={defaultParams}
              currency={storeData.defaultCurrency || ''}
            />
          )}
      </div>
      <div className="mt-5 mb-[25px] mx-0">
        {/* SHARE WIDGET */}
        {availableShareDates && (
          <SegmentContainer variant={'chosen'}>
            <Gap padding="8px 13px">
              <div className={headerContainerStyles}>
                <SegmentHeader
                  title={copyData?.glance_amazon_share_title}
                  subTitle={
                    isAValidDate(availableShareDates.startDate)
                      ? `${getFormattedDate(availableShareDates.startDate)} to ${getFormattedDate(
                          availableShareDates.endDate
                        )}`
                      : 'No data'
                  }
                />
              </div>
              <div className="mt-[14px] mb-[30px] mx-0">
                <Grid gridCols={isShare ? 5 : 1} rowGap="25px">
                  <AmazonShare
                    dateRange={availableShareDates}
                    isShare={isShare}
                    setFetching={(fetching) => setFetching(fetching)}
                  />
                </Grid>
              </div>
            </Gap>
          </SegmentContainer>
        )}
      </div>
      {/* SALES WIDGET */}
      {availableSalesDates && (
        <SegmentContainer variant="chosenWithoutSpacing">
          <Grid gridCols="1fr 2fr" items="start">
            <Gap padding="18px 25px">
              <div className={headerContainerStyles}>
                <SegmentHeader
                  title={copyData?.glance_sales_title}
                  subTitle={
                    isAValidDate(availableSalesDates.startDate)
                      ? `${getFormattedDate(availableSalesDates.startDate)} to ${getFormattedDate(
                          availableSalesDates.endDate
                        )}`
                      : 'No data'
                  }
                  tooltipData={{
                    status: 'HELP',
                    message: copyData?.glance_sales_tooltip
                  }}
                />
              </div>
              <Gap margin="20px 0 30px 0">
                <SalesSegment
                  dateRange={availableSalesDates}
                  currency={storeData.defaultCurrency || ''}
                  setFetching={(fetching) => setFetching(fetching)}
                />
              </Gap>
              {/* 
                <Gap margin="25px 0 0 0">
                  <Segment variant="small" title="% OF TOTAL SALES FROM DIGITAL COMMERCE" totalValue={35} unit={'%'} />
                </Gap> */}
            </Gap>
            <div style={{ height: 'auto', overflowX: 'auto' }}>
              <Gap padding="19px 15px 9px">
                <SalesGraph
                  dateRange={availableSalesDates}
                  setFetching={(fetching) => setFetching(fetching)}
                  currency={returnCurrencySymbol(storeData.defaultCurrency || '')}
                />
              </Gap>
            </div>
          </Grid>
        </SegmentContainer>
      )}
    </div>
  );
};

export default Glance;
